import { Routes } from '@angular/router';

import { AuthGuard } from '../../guard/auth.guard';
import { SmartReviewAccessGuard } from './core/guards/smart-review-access.guard';
import { SmartReviewAdminGuard } from './core/guards/smart-review-admin.guard';
import { ReviewComponent } from './review/review.component';
import { ReviewDetailComponent } from './review/components/review-detail/review-detail.component';
import { AdminShellComponent } from './admin/admin-shell/admin-shell.component';
import { AdminGatesComponent } from './admin/gates/admin-gates.component';
import { CurationQueueComponent } from './admin/curation/components/curation-queue/curation-queue.component';
import { CurationStatsComponent } from './admin/curation/components/curation-stats/curation-stats.component';
import { CostsPanelComponent } from './admin/costs/components/costs-panel/costs-panel.component';
import { AdminRepositoriesComponent } from './admin/repositories/admin-repositories.component';
import { ReviewDashboardComponent } from './dashboard/review-dashboard.component';

/**
 * Rotas da feature Smart Review. Espalhadas (spread) nos children do path `menu`
 * em app-routing.module.ts, preservando o aninhamento sob o MenuComponent e as
 * URLs `/menu/smart-review/...`. A área admin é servida pelo AdminShellComponent
 * (po-page + po-tabs nativos) e suas rotas filhas; o acesso `smartreview.admin`
 * é centralizado no SmartReviewAdminGuard.
 *
 * O SmartReviewAccessGuard só protege as rotas de review — o probe dele exige TFVC,
 * que é a regra correta para telas que mostram código-fonte.
 */
export const smartReviewRoutes: Routes = [
  {
    path: 'smart-review/reviews',
    component: ReviewComponent,
    canActivate: [SmartReviewAccessGuard],
    canActivateChild: [AuthGuard], canLoad: [AuthGuard]
  },
  {
    // Antes de `reviews/:type/:id`: com 3 segmentos, `reviews/dashboard/:algo` colidiria,
    // mas `reviews/dashboard` sozinho (2 segmentos) não bate na rota de detalhe — a ordem
    // aqui não decide nada. Fica antes por legibilidade, ao lado da listagem que a linka.
    path: 'smart-review/reviews/dashboard',
    component: ReviewDashboardComponent,
    // A rota diz qual audiência serve; a capability decide se quem abriu pode recebê-la
    // (ReviewDashboardComponent.resolveAudience). Aqui é sempre o recorte do próprio.
    data: { audience: 'self' },
    canActivate: [SmartReviewAccessGuard],
    canActivateChild: [AuthGuard], canLoad: [AuthGuard]
  },
  {
    path: 'smart-review/reviews/:type/:id',
    component: ReviewDetailComponent,
    canActivate: [SmartReviewAccessGuard],
    canActivateChild: [AuthGuard], canLoad: [AuthGuard]
  },
  {
    // Sem o SmartReviewAccessGuard de propósito: o probe dele (GET /reviews) passa pelo
    // gate TFVC no backend, e um admin sem acesso ao TFVC (curador/time de produto) tomaria
    // 403 antes de chegar aqui. O backend resolve /admin/* antes do gate TFVC; o
    // SmartReviewAdminGuard já exige `smartreview.admin`, que por definição dá tool access.
    path: 'smart-review/admin',
    component: AdminShellComponent,
    canActivate: [SmartReviewAdminGuard],
    canActivateChild: [AuthGuard], canLoad: [AuthGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'gates' },
      { path: 'gates', component: AdminGatesComponent },
      { path: 'curation/queue', component: CurationQueueComponent },
      { path: 'curation/stats', component: CurationStatsComponent },
      { path: 'costs', component: CostsPanelComponent },
      { path: 'dashboard', component: ReviewDashboardComponent, data: { audience: 'service' } },
      { path: 'repositories', component: AdminRepositoriesComponent }
    ]
  },
  // Redirecionamentos das URLs antigas (antes do split reviews/admin) para não
  // quebrar bookmarks/links externos. `:type/:id` (3 segmentos) não colide com
  // `smart-review/admin` nem com `smart-review/reviews/...`.
  { path: 'smart-review', pathMatch: 'full', redirectTo: 'smart-review/reviews' },
  { path: 'smart-review/:type/:id', redirectTo: 'smart-review/reviews/:type/:id' }
];
